import React, { useState, useEffect } from 'react';
import { MessageSquare, Mic, FileText, Radio, Database } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { toast } from '../ui/Toast';

const getGreeting = () => {
  const h = new Date().getHours();
  if (h >= 5 && h < 12) return 'Bonjour';
  if (h >= 12 && h < 18) return 'Bon après-midi';
  if (h >= 18 && h < 22) return 'Bonsoir';
  return 'Bonne nuit';
};

const WelcomeScreen = ({ user, profile, onStartConversation, onSuggestion, onStartCommMode, onStartVoice, onNavigateExpertise }) => {
  const { isDark } = useTheme();
  const prenom = user?.full_name?.split(' ')[0] || user?.username || profile?.profile_data?.prenom || null;
  const greeting = getGreeting();
  const [discoveryStatus, setDiscoveryStatus] = useState(null);
  const [creatingDiscovery, setCreatingDiscovery] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('oracle_token');
    if (!token) return;
    fetch('/api/expertise-bases/discovery/status', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(setDiscoveryStatus)
      .catch(() => {});
  }, []);

  const handleCreateDiscovery = async () => {
    if (creatingDiscovery) return;
    setCreatingDiscovery(true);
    try {
      const token = localStorage.getItem('oracle_token');
      const resp = await fetch('/api/expertise-bases/discovery', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await resp.json();
      if (!resp.ok) {
        if (resp.status === 409 && data.existingBaseId) {
          toast.info('Vous avez déjà une base découverte');
          if (onNavigateExpertise) onNavigateExpertise();
        } else {
          toast.error(data.error || 'Erreur lors de la création');
        }
        return;
      }
      toast.success('Base découverte créée — ajoutez vos premiers documents');
      if (onNavigateExpertise) onNavigateExpertise();
    } catch (err) {
      toast.error('Erreur réseau');
    } finally {
      setCreatingDiscovery(false);
    }
  };

  const suggestions = [
    { icon: <MessageSquare size={18} />, text: 'Parlez-moi de votre projet', color: '#3b82f6' },
    { icon: <Mic size={18} />, text: 'Dicter une note vocale', color: '#f97316', action: 'voice' },
    { icon: <FileText size={18} />, text: 'Résumer un document', color: '#8b5cf6' },
    { icon: <Radio size={18} />, text: 'Communiquer sur ITRIIA', color: '#10b981', mode: 'comm' },
  ];

  return (
    <div style={styles.container}>
      <div style={styles.inner}>
        <img src={isDark ? "/logo-itriia-white.png" : "/logo-itriia-dark.png"} alt="ITRIIA" style={{ height: 36, objectFit: 'contain', marginBottom: 12, opacity: 0.85, display: 'block', margin: '0 auto 12px auto' }} />
        <h2 style={styles.greeting}>
          {prenom ? `${greeting} ${prenom}, que puis-je faire pour vous ?` : 'Bienvenue sur ITRIIA'}
        </h2>
        <p style={styles.subtitle}>
          {prenom
            ? 'Posez une question, dictez une note ou lancez une analyse.'
            : 'Parlez-moi de vous ou posez votre première question. Je suis là pour vous aider.'}
        </p>

        <div style={styles.suggestions}>
          {suggestions.map((s, i) => (
            <button key={i} onClick={() => s.mode ? (onStartCommMode && onStartCommMode()) : s.action === 'voice' ? (onStartVoice && onStartVoice()) : (onSuggestion ? onSuggestion(s.text) : onStartConversation())}
              style={styles.suggestionBtn}>
              <div style={{ ...styles.suggestionIcon, color: s.color }}>{s.icon}</div>
              <span>{s.text}</span>
            </button>
          ))}

          {/* Base Découverte — masquée pour le rôle formation (promo 1, réversible).
              Correctif rôle/permissions/cercle (mig 135/136) : le backend gate désormais
              /api/expertise-bases/discovery par permissions.bases_expertise — sans ce
              contrôle, un compte sans cette permission cliquait "Créer ma base" et
              atterrissait sur un 403 (toast) suivi d'une page expertise vide. */}
          {user?.role !== 'formation' && (user?.role === 'admin_general' || user?.role === 'oracle_fondateur' || user?.permissions?.bases_expertise) && (
          <div style={{ marginTop: 16, padding: '16px 20px', background: 'var(--card-bg)', border: '1px solid rgba(139, 92, 246, 0.3)', borderRadius: 12, boxShadow: 'var(--shadow)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <div style={{ ...styles.suggestionIcon, color: '#8b5cf6' }}>
                <Database size={18} />
              </div>
              <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>
                {discoveryStatus?.hasDiscovery ? 'Ma base de spécialité' : 'Votre base de spécialité'}
              </span>
            </div>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5, margin: '0 0 12px 0' }}>
              {discoveryStatus?.hasDiscovery
                ? `Votre base contient ${discoveryStatus.base.document_count || 0}/2 documents. Ajoutez un document pour enrichir votre base — ITRIIA le structure, le résume et le rend interrogeable instantanément.`
                : 'Constituez votre propre base de connaissance. Déposez un rapport, un mémoire ou un document métier — ITRIIA le structure, le résume et le rend interrogeable. En quelques secondes, retrouvez n\'importe quelle information sans relire des dizaines de pages.'}
            </p>
            <button
              onClick={discoveryStatus?.hasDiscovery ? () => onNavigateExpertise && onNavigateExpertise() : handleCreateDiscovery}
              disabled={creatingDiscovery}
              style={{
                padding: '9px 20px', borderRadius: 8,
                border: '1px solid rgba(139, 92, 246, 0.5)',
                cursor: 'pointer',
                background: 'rgba(139, 92, 246, 0.12)',
                color: '#a78bfa',
                fontSize: 13, fontWeight: 600,
                boxShadow: '0 0 12px rgba(139, 92, 246, 0.25), inset 0 0 8px rgba(139, 92, 246, 0.08)',
                opacity: creatingDiscovery ? 0.6 : 1,
                transition: 'all 0.2s',
              }}
            >
              {creatingDiscovery ? 'Création...' : discoveryStatus?.hasDiscovery ? 'Ouvrir ma base' : 'Créer ma base'}
            </button>
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    height: '100%', padding: 40,
  },
  inner: { textAlign: 'center', maxWidth: 480 },
  greeting: { fontSize: 22, fontWeight: 600, color: 'var(--text)', marginBottom: 8 },
  subtitle: { fontSize: 15, color: 'var(--text-secondary)', marginBottom: 32, lineHeight: 1.5 },
  suggestions: { display: 'flex', flexDirection: 'column', gap: 10 },
  suggestionBtn: {
    display: 'flex', alignItems: 'center', gap: 14, padding: '14px 20px',
    background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 12,
    cursor: 'pointer', fontSize: 14, color: 'var(--text)', fontWeight: 500,
    transition: 'all 0.15s', textAlign: 'left', width: '100%',
    boxShadow: 'var(--shadow)',
  },
  suggestionIcon: {
    width: 36, height: 36, borderRadius: '50%', background: 'var(--input-bg)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
};

export default WelcomeScreen;
